
import React from "react";
import ReactDOM from "react-dom/client";
import { i18nManager } from "../../../../../i18n";
import { ApiResponseData, BaseService, SupportContactService, ICountry, ICurrency, ISupportContact as IContact, IPlatform, SanitizedResponse, ISupportContactAddress } from "../../../../../services";
import { NavLink, useSearchParams } from "react-router-dom";
import { ConfirmDialog, Elements, SidedContainer, Siren } from "../../../../shared";
import { Schemes, Subscriber, Utils } from "../../../../../utils";
import { FileInput, Alignment, ButtonManageRef, ObjectHelper, Scheme, Button, FormControl, DateTimeInput, TimeInput, FormGroup, TextInput, DOMHelper, DateTimePickerSelectionMode, DateInput } from "@rivtn/noseur";

export interface ContactsProps {
    platform: IPlatform;
}

function Contacts(props: ContactsProps) {
    const dialogErrorRef = React.useRef<HTMLDivElement>(null);
    const contactService = SupportContactService.getInstance();
    const activeDialogMutableRoot = React.useRef<ReactDOM.Root>(null);
    const [contacts, setContacts] = React.useState<ApiResponseData<IContact>>({});
    const [searchParams, setSearchParams] = useSearchParams(window.location.search);
    const labels = i18nManager.Labels.dashboard.platform_management.management.support.contacts;
    labels.sided_labels.dropdown_form![0].control.options = [{ label: i18nManager.Labels.common.all, value: "" }, ...i18nManager.Languages];

    return SidedContainer({
        data: contacts,
        searchParams,
        setSearchParams,
        onAction: manageContact,
        queryData: fetchContacts,
        labels: labels.sided_labels,
        firstStyle: {
            marginTop: 15,
            padding: "0px 20px 20px",
            border: "1px solid #F0F0F0",
        },
        secondStyle: {
            padding: 15,
            marginTop: 15,
            border: "1px solid #F0F0F0",
        },
        dataTemplate: (contact: IContact) => {
            return (<div className="hera-item" onClick={() => {
                manageContact(contact);
                Utils.updateSearchParams("adi", contact.external_id, setSearchParams);
            }}>
                <div>
                    <div className="content" style={{ display: "flex", flexDirection: "column" }}>
                        <span><b>{labels.language}:</b> {i18nManager.Languages.find((l) => l.value == contact.language)?.label}</span>
                        <span><b>{labels.physical_address}:</b> {contact.physical_address}</span>
                        <span><b>{labels.description}:</b> {contact.description}</span>
                        <span><b>{labels.weekdays_op_hours}:</b> {contact.weekdays_opening_hours?.open} - {contact.weekdays_opening_hours?.close}</span>
                        <span><b>{labels.weekends_op_hours}:</b> {contact.weekends_opening_hours?.open} - {contact.weekends_opening_hours?.close}</span>
                        <span><b>{labels.holiday_op_hours}:</b> {contact.holiday_opening_hours?.open} - {contact.holiday_opening_hours?.close}</span>
                        <span><b>{labels.addresses}:</b></span>
                        {contact.addresses.map((address) => (<>
                            <span style={{ marginLeft: 10 }}><b>{labels.email}:</b> {address.email}, <b>{labels.phone}:</b> {address.phone}, </span>
                        </>))}
                    </div>
                </div>
            </div>);
        },
    } as any);

    function fetchContacts() {
        contactService.querySupportContacts(Utils.normalizeUrlParams(searchParams)).then(({ apiResponse }: SanitizedResponse<IContact>) => {
            setContacts(apiResponse.data!);
        }).catch(BaseService.reportError).finally(() => Subscriber.report(Subscriber.KEYS.QUERY_REQUEST_PROMISE_COMPLETED));
    }

    function manageContact(contact: IContact = {} as any) {
        const isUpdate = !!contact.description;
        const dialogLabels = labels.dialogs.add_edit;
        contact.language = contact.language ?? searchParams.get("language");

        function renderAddresses(contact: IContact) {
            if (!contact.addresses || !activeDialogMutableRoot.current) return;
            const addresses = contact.addresses.map((address: ISupportContactAddress, index: number) => {
                return (<div key={DOMHelper.uniqueElementId("cont")} style={{ marginTop: 10, borderBottom: "1px solid #F0F0F0", paddingBottom: 20 }}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                        <i className="fa fa-trash" style={{ cursor: "pointer", color: "#EB5959" }} onClick={() => {
                            contact.addresses.splice(index, 1);
                            renderAddresses(contact);
                        }} />
                        <span style={{ marginLeft: 10 }}>{dialogLabels.address}</span>
                    </div>
                    <FormGroup className="form">
                        <FormControl fill scheme={Schemes.RIVTN_QUIRINUS} validStyle={{ borderColor: "transparent" }} style={{ marginTop: 15 }} contentStyle={{ borderRadius: 0 }}>
                            <TextInput fill noStyle placeholder={dialogLabels.email} defaultValue={address.email} onInputComplete={(v) => {
                                address.email = v;
                            }} />
                        </FormControl>
                        <FormControl fill scheme={Schemes.RIVTN_QUIRINUS} validStyle={{ borderColor: "transparent" }} style={{ marginTop: 15 }} contentStyle={{ borderRadius: 0 }}>
                            <TextInput fill noStyle placeholder={dialogLabels.phone} defaultValue={address.phone} onInputComplete={(v) => {
                                address.phone = v;
                            }} />
                        </FormControl>
                    </FormGroup>
                </div>);
            });
            activeDialogMutableRoot.current.render(addresses);
        }

        const dialog = ConfirmDialog({
            closable: true,
            title: (isUpdate ? dialogLabels.edit_title : dialogLabels.add_title),
            confirmLabel: (isUpdate ? dialogLabels.update_action_label : dialogLabels.add_action_label),
            style: { minWidth: 800, maxWidth: "80%" },
            content: (<div className="add-new-role">
                {Elements.buildConfirmDialogForm(dialogLabels.form, (name: string, value: any, searching: any) => {
                    dialogErrorRef.current!.innerText = " ";
                    (contact as any)[name] = value;
                    return false;
                }, contact, undefined, { marginTop: 0 }, {
                    "language": i18nManager.Languages
                })}
                <FormGroup className="form">
                    <div style={{ marginTop: 15 }}>{dialogLabels.weekdays_op_hours}</div>
                    <FormControl fill scheme={Schemes.RIVTN_QUIRINUS} leftContent={"fa fa-clock"} validStyle={{ borderColor: "transparent" }} contentStyle={{ borderRadius: 0 }}>
                        <TimeInput fill noStyle selectionMode={DateTimePickerSelectionMode.RANGE} onSelectDate={(options) => {
                            console.log("THE WEEKDAYS", options.formatDate(options.fromDate!));
                        }} />
                    </FormControl>
                    <div style={{ marginTop: 15 }}>{dialogLabels.weekends_op_hours}</div>
                    <FormControl fill scheme={Schemes.RIVTN_QUIRINUS} leftContent={"fa fa-clock"} validStyle={{ borderColor: "transparent" }} contentStyle={{ borderRadius: 0 }}>
                        <TimeInput fill noStyle onSelectDate={(options) => {
                            console.log("THE WEEKENDS", options.formatDate(options.fromDate!));
                        }} />
                    </FormControl>
                    <div style={{ marginTop: 15 }}>{dialogLabels.holiday_op_hours}</div>
                    <FormControl fill scheme={Schemes.RIVTN_QUIRINUS} leftContent={"fa fa-clock"} validStyle={{ borderColor: "transparent" }} contentStyle={{ borderRadius: 0 }}>
                        <TimeInput fill noStyle onSelectDate={(options) => {
                            console.log("THE HOLIDAYS", options.formatDate(options.fromDate!));
                        }} />
                    </FormControl>
                </FormGroup>
                <div ref={dialogErrorRef} className="error"> </div>
            </div>),
            rightContent: (<div style={{ display: "flex", flexDirection: "column", height: "100%", borderLeft: "1px solid #F0F0F0", marginTop: 30, marginLeft: 5, paddingLeft: 25 }}>
                <div style={{ display: "flex", flexDirection: "column" }} ref={(r) => {
                    if (!r) return;
                    ObjectHelper.resolveRef(activeDialogMutableRoot, ReactDOM.createRoot(r));
                    renderAddresses(contact);
                }} />
                <span className="link" style={{ marginTop: 20 }} onClick={() => {
                    contact.addresses = (contact.addresses ?? []).concat([{} as any])
                    renderAddresses(contact);
                }}>{dialogLabels.add_new_address}</span>
            </div>),
            footerElements: (isUpdate ? (<Button scheme={Scheme.DANGER} text={dialogLabels.delete} style={{ padding: "10px 30px" }} onClick={() => {
                dialog.hide(() => deleteContact(contact));
            }} />) : undefined),
            onConfirm: (button?: ButtonManageRef) => {
                dialogErrorRef.current!.innerText = " ";
                if (Object.keys(contact).length < 3) {
                    dialogErrorRef.current!.innerText = dialogLabels.fill_required_fields;
                    return false;
                }
                button?.setLoadingState(true);
                contact.platform_id = props.platform.platform_id;
                const method = (isUpdate ? contactService.updateSupportContact : contactService.createSupportContact).bind(contactService);
                method(contact).then(({ }: SanitizedResponse<IContact>) => {
                    Siren.alert(isUpdate ? dialogLabels.update_successful : dialogLabels.add_successful);
                    dialog.destroy(fetchContacts);
                }).catch(({ errors }: SanitizedResponse<any>) => {
                    dialogErrorRef.current!.innerText = errors?.join("\n") ?? "";
                }).finally(() => {
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

    function deleteContact(contact: IContact) {
        const dialog = ConfirmDialog({
            confirmScheme: Scheme.DANGER,
            title: labels.dialogs.delete.title,
            cancelLabel: i18nManager.Labels.common.cancel,
            desc: labels.dialogs.delete.desc.replace("${description}", contact.description)
                .replace("${language}", contact.language),
            onCancel: () => {
                dialog.hide(() => manageContact(contact));
            },
            onConfirm: (button?: ButtonManageRef) => {
                button?.setLoadingState(true);
                contactService.deleteSupportContact(contact.external_id).then(({ }: SanitizedResponse<any>) => {
                    Siren.alert(labels.dialogs.delete.success);
                    dialog.hide(fetchContacts);
                }).catch(BaseService.reportError).finally(() => {
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

}

export default Contacts;
